import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Analytics } from '@vercel/analytics/react';
import './App.css';
import Header from './components/Header';
import Footer from './components/Footer';
import MatrixRain from './components/MatrixRain';
import soundEffects from './utils/soundEffects';

// ── Core pages (critical) ────────────────────────────────────────────────────
const HomePage = lazy(() => import('./pages/HomePage'));
const PracticePage = lazy(() => import('./pages/PracticePage'));
const LessonPage = lazy(() => import('./pages/LessonPage'));

// ── Secondary pages ──────────────────────────────────────────────────────────
const AboutPage = lazy(() => import('./pages/AboutPage'));
const FeaturesPage = lazy(() => import('./pages/FeaturesPage'));
const BugModePage = lazy(() => import('./pages/BugModePage'));
const TopicPage = lazy(() => import('./pages/TopicPage'));
const BlogPage = lazy(() => import('./pages/BlogPage'));
const BlogPost = lazy(() => import('./pages/BlogPost'));

// ── SEO / informational pages ────────────────────────────────────────────────
const TypingTestPage = lazy(() => import('./pages/TypingTestPage'));
const TypingSpeedPage = lazy(() => import('./pages/TypingSpeedPage'));
const TypingPracticePage = lazy(() => import('./pages/TypingPracticePage'));
const WPMPage = lazy(() => import('./pages/WPMPage'));
const WpmCalculatorPage = lazy(() => import('./pages/WpmCalculatorPage'));
const TypingTipsPage = lazy(() => import('./pages/TypingTipsPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage'));
const TermsPage = lazy(() => import('./pages/TermsPage'));

// ── Landing / topic pages ────────────────────────────────────────────────────
const BestTypingRacingGame = lazy(() => import('./pages/BestTypingRacingGame'));
const UltimateTypingPractice = lazy(() => import('./pages/UltimateTypingPractice'));
const BestTypingTest = lazy(() => import('./pages/BestTypingTest'));
const BestTypingSite = lazy(() => import('./pages/BestTypingSite'));
const BestTypingPractice = lazy(() => import('./pages/BestTypingPractice'));

// ── 404 ─────────────────────────────────────────────────────────────────────
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));

// Loading fallback
const PageLoader = () => (
  <div style={{
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '50vh',
    color: '#00ff00',
    fontFamily: 'monospace',
    fontSize: '13px'
  }}>
    Loading matrix data...
  </div>
);

const ScrollReset = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    const container = document.querySelector('.main-content');
    if (container) container.scrollTop = 0;
  }, [pathname]);
  return null;
};

function App() {
  useEffect(() => {
    const handleClick = () => {
      soundEffects.resume();
      soundEffects.playClickSound();
    };
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, []);

  return (
    <Router>
      <ScrollReset />
      <div className="App">
        <Helmet>
          <html lang="en" />
          <meta charSet="utf-8" />
          <meta name="viewport" content="width=device-width, initial-scale=1" />
          <meta name="theme-color" content="#00ff00" />
          <meta name="author" content="Ishan Kafle" />
          <meta name="robots" content="index, follow" />
          {/* No base canonical here — each page sets its own via its Helmet / SEO component */}
        </Helmet>

        <MatrixRain />
        <div className="game-container">
          <Header />
          <main
            className="main-content"
            role="main"
            style={{
              // ...mi,
              minHeight: "100vh",
            }}
          >
            <Suspense fallback={<PageLoader />}>
              <Routes>
                {/* Core app routes */}
                <Route path="/" element={<HomePage />} />
                <Route path="/about" element={<AboutPage />} />
                <Route path="/features" element={<FeaturesPage />} />
                <Route path="/bugmode" element={<BugModePage />} />
                <Route path="/practice" element={<PracticePage />} />
                <Route path="/lessons" element={<LessonPage />} />
                <Route path="/blog" element={<BlogPage />} />
                <Route path="/blog/:slug" element={<BlogPost />} />
                <Route path="/topics/:slug" element={<TopicPage />} />

                {/* SEO / informational pages */}
                <Route path="/typing-test" element={<TypingTestPage />} />
                <Route path="/typing-speed" element={<TypingSpeedPage />} />
                <Route path="/typing-practice" element={<TypingPracticePage />} />
                <Route path="/wpm" element={<WPMPage />} />
                <Route path="/wpm-test" element={<WPMPage />} />
                <Route path="/tools/wpm-calculator" element={<WpmCalculatorPage />} />
                <Route path="/typing-tips" element={<TypingTipsPage />} />
                <Route path="/contact" element={<ContactPage />} />
                <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
                <Route path="/terms" element={<TermsPage />} />

                {/* Landing / topic pages */}
                <Route path="/best-typing-racing-game" element={<BestTypingRacingGame />} />
                <Route path="/ultimate-typing-practice" element={<UltimateTypingPractice />} />
                <Route path="/best-typing-test" element={<BestTypingTest />} />
                <Route path="/best-typing-site" element={<BestTypingSite />} />
                <Route path="/best-typing-practice" element={<BestTypingPractice />} />

                {/* 404 catch-all */}
                <Route path="*" element={<NotFoundPage />} />
              </Routes>
            </Suspense>
          </main>
          <Footer />
        </div>
      </div>
      <Analytics />
    </Router>
  );
}

export default App;
